// ─────────────────────────────────────────────────────────────────────────────
// MEDIAPIPE HOLISTIC — unified pose + face + hands in one inference call
// Replaces: getPoseLandmarker() + getFaceLandmarker() + separate hand detection
//
// HolisticLandmarker (tasks-vision ≥ 0.10.3) runs a single WASM graph that
// produces all three landmark sets simultaneously with lower total overhead
// than running three separate landmarkers.
// ─────────────────────────────────────────────────────────────────────────────
import {
  HolisticLandmarker,
  FilesetResolver,
  type HolisticLandmarkerResult,
} from "@mediapipe/tasks-vision";

export type { HolisticLandmarkerResult };

export interface HolisticResult {
  poseLandmarks:        Array<{ x: number; y: number; z: number; visibility?: number }>;
  faceLandmarks:        Array<{ x: number; y: number; z: number }>;
  leftHandLandmarks:    Array<{ x: number; y: number; z: number }>;
  rightHandLandmarks:   Array<{ x: number; y: number; z: number }>;
  timestamp:            number;
}

const HOLISTIC_MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/holistic_landmarker/holistic_landmarker/float16/latest/holistic_landmarker.task";

let _holistic: HolisticLandmarker | null = null;
let _loading = false;
let _failed  = false;

export let _holisticLandmarker: HolisticLandmarker | null = null;

export async function getHolisticLandmarker(): Promise<HolisticLandmarker | null> {
  if (_holistic)  return _holistic;
  if (_failed)    return null;
  if (_loading)   {
    await new Promise<void>(res => {
      const iv = setInterval(() => {
        if (_holistic || _failed) { clearInterval(iv); res(); }
      }, 50);
    });
    return _holistic;
  }

  _loading = true;
  try {
    const vision = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm"
    );

    _holistic = await HolisticLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: HOLISTIC_MODEL_URL,
        delegate: "GPU",
      },
      runningMode:              "VIDEO",
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence:  0.5,
      // minTrackingConfidence not in HolisticLandmarkerOptions
      minFaceDetectionConfidence: 0.5,
      minFacePresenceConfidence:  0.5,
      minHandLandmarksConfidence: 0.5,
      outputFaceBlendshapes:      false,
    });

    _holisticLandmarker = _holistic;
    console.log("[Holistic] ✓ Unified pose+face+hands ready");
  } catch (err) {
    console.warn("[Holistic] Failed to load — falling back to separate landmarkers", err);
    _failed = true;
  }

  _loading = false;
  return _holistic;
}

// ── Run holistic detection on a video frame ───────────────────────────────────
export function detectHolistic(
  video: HTMLVideoElement,
  timestamp: number
): HolisticResult | null {
  if (!_holistic) return null;

  try {
    const result: HolisticLandmarkerResult = _holistic.detectForVideo(video, timestamp);

    return {
      poseLandmarks:      (result.poseWorldLandmarks?.[0]  ?? result.poseLandmarks?.[0]  ?? []) as HolisticResult["poseLandmarks"],
      faceLandmarks:      (result.faceLandmarks?.[0]       ?? []) as HolisticResult["faceLandmarks"],
      leftHandLandmarks:  (result.leftHandLandmarks?.[0]   ?? []) as HolisticResult["leftHandLandmarks"],
      rightHandLandmarks: (result.rightHandLandmarks?.[0]  ?? []) as HolisticResult["rightHandLandmarks"],
      timestamp,
    };
  } catch {
    return null;
  }
}

export function isHolisticReady(): boolean {
  return !!_holistic;
}

export function resetHolistic() {
  _holistic?.close();
  _holistic = null;
  _holisticLandmarker = null;
  _loading = false;
  _failed  = false;
}
